import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { BusinessPartnerRegion } from 'src/core/types/business-partner.types';
import { ConsentStatus, UserData, UserValidationStatus } from 'src/core/types/user.types';
import { environment } from 'src/environments/environment';

const BUSINESS_PARTNER_MANAGER_ROLE_TO_REGION: Record<string, BusinessPartnerRegion> = {
  SH103_MADiS_165789_G: 'AP',
  SH103_MADiS_165788_G: 'LAM',
  SH103_MADiS_165787_G: 'NAM',
  SH103_MADiS_165790_G: 'EMEA',
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiBaseUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port !== "" ? ":" + "8080" : ""}/data`;
  redirectUrl: string = '/';
  userData: UserData;
  consent: boolean = false;

  constructor(private http: HttpClient) { }

  private getAuthorizedRolePrefixes(): string[] {
    const configuredRoles = new Set<string>();
    const configuredAuthRole = environment.authRole as string | string[] | undefined;

    if (Array.isArray(configuredAuthRole)) {
      configuredAuthRole.map((role) => `${role}`.trim()).filter(Boolean).forEach((role) => configuredRoles.add(role));
    } else if (configuredAuthRole) {
      configuredRoles.add(`${configuredAuthRole}`.trim());
    }

    if (environment.businessPartnerMode) {
      const businessPartnerMailAuthRole = (environment as { businessPartnerMailAuthRole?: string }).businessPartnerMailAuthRole;
      const businessPartnerDomainAuthRole = (environment as { businessPartnerDomainAuthRole?: string }).businessPartnerDomainAuthRole;

      [businessPartnerMailAuthRole, businessPartnerDomainAuthRole]
        .map((role) => `${role ?? ''}`.trim())
        .filter(Boolean)
        .forEach((role) => configuredRoles.add(role));
    }

    return Array.from(configuredRoles);
  }

  private getGroupFilter(): string {
    const authorizedRoleFilters = this.getAuthorizedRolePrefixes()
      .map((roleName) => `startswith(displayName,'${roleName}')`);
    const managerRoleFilters = Object.keys(BUSINESS_PARTNER_MANAGER_ROLE_TO_REGION)
      .map((roleName) => `displayName eq '${roleName}'`);

    return [...authorizedRoleFilters, ...managerRoleFilters].join(' or ');
  }

  private isAuthorizedGroup(displayName: string): boolean {
    return this.getAuthorizedRolePrefixes().some((roleName) => displayName.startsWith(roleName));
  }

  private getGroupDisplayNames(): string[] {
    return (this.userData?.groups ?? [])
      .map((group) => `${group?.displayName ?? ''}`.trim())
      .filter(Boolean);
  }

  hasDirectoryGroup(roleName: string): boolean {
    const trimmedRoleName = `${roleName ?? ''}`.trim();

    if (!trimmedRoleName) {
      return false;
    }

    return this.getGroupDisplayNames().some((displayName) => displayName.startsWith(trimmedRoleName));
  }

getUserData() {
  return forkJoin([
    this.http.get('https://graph.microsoft.com/v1.0/me?$select=department,givenName,surname,mail,id,country,extension_fda241a84ec449a4ad6ea456af6e0161_employeeType'),
    this.http.get(
      `https://graph.microsoft.com/v1.0/me/memberOf?$count=true&$filter=${encodeURIComponent(this.getGroupFilter())}&$select=displayName,id`,
      { headers: { ConsistencyLevel: "eventual" } }
    ),
    this.http.get("https://graph.microsoft.com/v1.0/me/photo/$value", { responseType: 'blob' }).pipe(
      // Profile photo is optional; never block login if this request fails.
      catchError(err => {
        if (err?.status === 404) {
          console.warn('No profile photo found for user.');
        } else {
          console.warn(`Profile photo request failed (status: ${err?.status ?? 'unknown'}). Continuing without photo.`);
        }
        return of(null);
      })
    )
  ]).pipe(
    switchMap(([basic, groups, photo]: [any, any, Blob | null]) =>
      from(this.hashUserId(basic['id'])).pipe(
        map((anonymizedId) => {
          const addAnonymizedId = {
            ...basic,
            anonymizedId,
          };

          const photoUrl = photo ? URL.createObjectURL(photo) : null;

          return {
            basic: addAnonymizedId,
            groups: groups?.value,
            photo: photoUrl,
            authorized: (groups?.value ?? []).some((group: { displayName?: string }) => this.isAuthorizedGroup(`${group?.displayName ?? ''}`)),
            consented: null,
            termsOfUseAccepted: null,
          };
        })
      )
    ),
    tap((result: UserData) => {
      // store once, using the mapped result
      this.userData = result;
    })
  );
}


  getUserValidation(accountId: string): Observable<UserValidationStatus> {

    return this.http.get<Partial<UserValidationStatus>>(`${this.apiBaseUrl}/getUserValidation`).pipe(
      map((response) => ({
        consent: (response?.consent ?? 'no-entry') as ConsentStatus,
        termsOfUseAccepted: response?.termsOfUseAccepted === true,
      })),
      catchError(err => {
        console.error('User validation check failed', err);
        return of({
          consent: 'denied' as const,
          termsOfUseAccepted: !environment.businessPartnerMode,
        });
      })
    );
  }

  checkTrackingConsent(accountId: string): Observable<ConsentStatus> {

    return this.http.get<{ consent?: string }>(`${this.apiBaseUrl}/getconsent`).pipe(
      map((response) => (response?.consent ?? 'no-entry') as ConsentStatus),
      catchError(err => {
        console.error('Consent check failed', err);
        return of('denied' as const);
      })
    );
  }

  updateTrackingConsent(accountId: string, consent: boolean): Observable<{ event: string; success: boolean }> {
    return this.http.put<{ event: string; success: boolean }>(`${this.apiBaseUrl}/updateconsent`, { consent });
  }

  updateUserValidation(accountId: string, consent: boolean, termsOfUseAccepted: boolean): Observable<{ event: string; success: boolean }> {
    return this.http.put<{ event: string; success: boolean }>(`${this.apiBaseUrl}/updateUserValidation`, {
      consent,
      termsOfUseAccepted,
    });
  }

  async hashUserId(userId: string): Promise<string> {
    const SALT = 'static-salt';
    const data = new TextEncoder().encode(SALT + userId);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  getBusinessPartnerManagerRegions(): BusinessPartnerRegion[] {
    const matchedRegions = this.getGroupDisplayNames()
      .map((groupName) => BUSINESS_PARTNER_MANAGER_ROLE_TO_REGION[groupName as keyof typeof BUSINESS_PARTNER_MANAGER_ROLE_TO_REGION])
      .filter((region): region is BusinessPartnerRegion => Boolean(region));

    return Array.from(new Set(matchedRegions));
  }

  hasSingleBusinessPartnerManagerRegionRole(): boolean {
    return this.getBusinessPartnerManagerRegions().length === 1;
  }

  hasMultipleBusinessPartnerManagerRegionRoles(): boolean {
    return this.getBusinessPartnerManagerRegions().length > 1;
  }

  getBusinessPartnerManagerRegion(): BusinessPartnerRegion | null {
    const regions = this.getBusinessPartnerManagerRegions();
    return regions.length === 1 ? regions[0] : null;
  }

}